import { Modal } from "components/core/basic/RACModal";
import { type GVAdvertisement } from "modules/advertising/types";
import { useState } from "react";
import { css } from "../../../styled-system/css";
import { Flex } from "../../../styled-system/jsx";
import { Button } from "components/core/basic/Button";
import { NumberInput } from "components/fields/base/NumberInput";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { errorToast, successToast } from "utils/toasts";
import api from "utils/api";
import moment from "moment";
import { formatCurrency } from "utils/formatNumber";
import { useAppSelector } from "redux/hooks";
import { FaPencil } from "react-icons/fa6";

type Props = {
  ad: GVAdvertisement;
  defaultSuggestedBudget: number;
  isUnmanagedAd: boolean;
};

type BudgetOverrideUpdateBody = {
  id: number;
  custom_api_request: string;
  daily_budget_override: number | null;
  daily_budget_override_valid_until: string | null;
};

const BudgetIncreaseModal = ({ ad, defaultSuggestedBudget, isUnmanagedAd = false }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [dailyBudgetAmount, setDailyBudgetAmount] = useState(defaultSuggestedBudget);

  const queryClient = useQueryClient();
  const { marketPlace } = useAppSelector((state) => state.marketPlace);

  const existingDailyIncrease = ad.daily_budget_override;

  const reset = () => {
    setDailyBudgetAmount(defaultSuggestedBudget);
    setIsOpen(false);
    setIsUpdateMode(false);
  };

  const updateHandler = useMutation({
    mutationFn: async () => {
      const body: BudgetOverrideUpdateBody = {
        id: ad.ad_id,
        custom_api_request: "update_advertisement",
        daily_budget_override: null,
        daily_budget_override_valid_until: null,
      };

      if (!existingDailyIncrease || isUpdateMode) {
        body["daily_budget_override"] = dailyBudgetAmount;
        body["daily_budget_override_valid_until"] = moment().endOf("day").format("YYYY-MM-DDTHH:mm:ss.SSS");
      }

      return await api.put("/api/gvads/advproduct/", body);
    },

    onSuccess: () => {
      reset();
      successToast({
        message: "Updating Media Plan Budget",
      });
      void queryClient.invalidateQueries({ queryKey: ["ad_report"] });
    },

    onError: (error) => {
      errorToast({
        message: "Failed to Update Media Plan Budget",
        error,
      });
    },
  });

  return (
    <Flex alignItems={"center"}>
      <div className={css({ margin: "auto" })}>
        <Button
          size={"small"}
          onPress={() => {
            if (existingDailyIncrease) {
              updateHandler.mutate();
            } else {
              setIsOpen(true);
            }
          }}
          className={css({ margin: "auto", marginTop: "xsmall" })}
          isPending={updateHandler.isPending}
        >
          {existingDailyIncrease ? "Revert Today's Budget Increase" : "Increase Today's Budget"}
        </Button>
        {existingDailyIncrease ?
          <small className={css({ display: "block", padding: "small", textAlign: "center" })}>
            Today's budget is temporarily increased to {formatCurrency(existingDailyIncrease, marketPlace)}{" "}
            <FaPencil
              onClick={() => {
                setIsUpdateMode(true);
                setIsOpen(true);
              }}
              className={css({ cursor: "pointer" })}
            />
          </small>
        : null}
      </div>
      <Modal
        isOpen={isOpen}
        onOpenChange={(isOpen) => !isOpen && setIsOpen(false)}
        title={`Update Today's Budget - ${ad.ad_name}`}
        className={css({ width: "55vw" })}
      >
        <Flex className={css({ padding: "xlarge" })} gap={"medium"} direction={"column"} alignItems={"center"}>
          {isUnmanagedAd ?
            <>
              <p className={css({ textAlign: "center" })}>
                This feature is only support for media plans managed by Trellis. To enable it, enroll this media plan in
                bid management.
              </p>
              <Flex className={css({ paddingTop: "large" })} gap={"xlarge"}>
                <Button onPress={() => reset()}>Continue</Button>
              </Flex>
            </>
          : <>
              <p className={css({ textAlign: "center" })}>
                Enter a temporary budget for today using the field below. The media plan's budget will revert back to
                it's regular amount tomorrow.
              </p>
              <div className={css({ width: "200px" })}>
                <NumberInput
                  onChange={(value) => setDailyBudgetAmount(value)}
                  value={dailyBudgetAmount}
                  label={"Today's Budget"}
                  placeholder={"Enter amount"}
                  minValue={0}
                />
              </div>
              <Flex className={css({ paddingTop: "large" })} gap={"xlarge"}>
                <Button variant={"secondary"} onPress={() => reset()}>
                  Cancel
                </Button>
                <Button onPress={() => updateHandler.mutate()}>Update Budget</Button>
              </Flex>
            </>
          }
        </Flex>
      </Modal>
    </Flex>
  );
};

export default BudgetIncreaseModal;
